import React from "react";
import { Box, Typography } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view";
import {
  bomFolderNodeId,
  modelingNodeId,
  modelingTwoDDrawingNodeId,
} from "../utils/treeNodeIds";
import { drawingNumberOf } from "../utils/treeLabels";
import { TreeNodeIcon } from "./TreeNodeIcon";
import { DrawingNode } from "./DrawingNodes";

// The "2D Drawing" branch inside "3D Modeling" - a placeholder row for now:
// nothing behind it exists yet, so it carries no selection target and a
// click does nothing.
const TwoDDrawingNode = ({ wbs }) => {
  const id = modelingTwoDDrawingNodeId(wbs.id);

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<TreeNodeIcon />}
      label={
        <Typography
          variant="body2"
          noWrap
          title="2D Drawing"
          sx={{ minWidth: 0 }}
        >
          2D Drawing
        </Typography>
      }
    />
  );
};

// The "BOM" folder inside "3D Modeling" - the project's drawings. Each child
// is a DrawingNode, so a click navigates straight to
// /plant/:plantId/drawing/:drawingId (BomPage) with no confirmation.
export const BomFolderNode = ({ plant, wbs, drawings }) => {
  const id = bomFolderNodeId(wbs.id);
  const list = Array.isArray(drawings) ? drawings : [];

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<TreeNodeIcon />}
      label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            BOM
          </Typography>
          <Typography variant="caption" color="text.disabled">
            ({list.length})
          </Typography>
        </Box>
      }
    >
      {list.length > 0 ? (
        list.map((drawing, index) => (
          <DrawingNode
            key={drawing?.id ?? `${drawingNumberOf(drawing)}-${index}`}
            plant={plant}
            wbs={wbs}
            drawing={drawing}
            index={index}
          />
        ))
      ) : (
        <TreeItem
          key={`${id}-empty`}
          nodeId={`${id}-empty`}
          label={
            <Typography variant="caption" color="text.secondary">
              No drawings
            </Typography>
          }
        />
      )}
    </TreeItem>
  );
};

// The "3D Modeling" branch under a Design Automation WBS. Like the
// procurement folders it carries no selection target - a click only expands
// it.
export const ModelingFolderNode = ({ plant, wbs, drawings }) => {
  const id = modelingNodeId(wbs.id);

  return (
    <TreeItem
      key={id}
      nodeId={id}
      icon={<TreeNodeIcon />}
      label={
        <Typography
          variant="body2"
          noWrap
          title="3D Modeling"
          sx={{ minWidth: 0 }}
        >
          3D Modeling
        </Typography>
      }
    >
      <TwoDDrawingNode wbs={wbs} />
      <BomFolderNode plant={plant} wbs={wbs} drawings={drawings} />
    </TreeItem>
  );
};

export default ModelingFolderNode;